import {
  Button,
  CloseButton,
  Flex,
  Heading,
  Skeleton,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
} from "@chakra-ui/react";
import { usePrevious } from "@react-hookz/web";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { TaskElement } from "../../../hooks/api/karl-farm/useAllTasks";
import { useDeleteTaskMutation } from "../../../hooks/api/karl-farm/useDeleteTaskMutation";
import { useEditTaskMutation } from "../../../hooks/api/karl-farm/useEditTaskMutation";
import { dateToScore, useScores } from "../../../hooks/api/karl-farm/useScores";
import { useTranslation } from "../../../i18n/useTranslation";
import { addDateAndTime } from "../../../utils/dates";
import { DrawerContent, DrawerFooter, DrawerHeader } from "../../Drawer";
import { TaskForm, TaskFormData, floorMinutesTo30s } from "../TaskForm";
import { Conditions } from "./Conditions";

const taskDtoToTaskFormData: (task: TaskElement) => TaskFormData = (task) => {
  const startMillis = task.time.start * 1000;
  const startDate = new Date(startMillis);

  return {
    title: task.attributes.title || "",
    task: task.attributes.task,
    crop: task.attributes.crop,
    tillingDepth: task.attributes.tilling_depth,
    dropSize: task.attributes.drop_size,
    startDate: startDate,
    startTime: floorMinutesTo30s(
      startDate.getHours() * 60 + startDate.getMinutes()
    ).toString(),
    duration: task.time.duration.toString(),
    fields: task.fields.map((field) => field.id),
    users: task.users.map((user) => user.id),
    machines: task.machines.map((machine) => machine.id),
    notes: task.attributes.notes || "",
  };
};

type EditTaskDrawerProps = {
  calendarStartDate: Date;
  task: TaskElement | null;
  onClose: () => void;
};
export const EditTaskDrawer: FC<EditTaskDrawerProps> = ({
  calendarStartDate,
  task: unsafeTask,
  onClose,
}) => {
  const { t } = useTranslation();

  const latestSafeTaskRef = useRef<TaskElement>(unsafeTask as TaskElement); // Cast okay, as it's always defined in the first render
  const task = useMemo(() => {
    const safeTask = unsafeTask ?? latestSafeTaskRef.current;
    latestSafeTaskRef.current = safeTask;
    return latestSafeTaskRef.current;
  }, [unsafeTask]);
  const previousTask = usePrevious(task);

  const methods = useForm<TaskFormData>({
    shouldUnregister: true,
  });
  const { handleSubmit } = methods;

  const { mutate } = useEditTaskMutation();

  const handleValidSubmit: SubmitHandler<TaskFormData> = (formData) => {
    const {
      title,
      crop,
      tillingDepth,
      dropSize,
      fields,
      startDate,
      startTime,
      duration,
      users,
      machines,
      notes,
    } = formData;

    const localizedStartDateTime = addDateAndTime(
      startDate,
      parseInt(startTime)
    );
    const startDateTimeEpochSeconds = Math.floor(
      localizedStartDateTime.getTime() / 1000
    );

    mutate({
      id: task.id,
      body: {
        attributes: {
          title,
          task: formData.task,
          crop,
          tilling_depth: tillingDepth,
          drop_size: dropSize,
          notes,
        },
        time: {
          start: startDateTimeEpochSeconds,
          duration: parseInt(duration),
        },
        fields: fields.map((fieldId) => ({ id: fieldId })),
        users: users.map((userId) => ({ id: userId })),
        machines: machines.map((machineId) => ({ id: machineId })),
      },
    });

    onClose();
  };

  const defaultValues: TaskFormData = useMemo(
    () => taskDtoToTaskFormData(task),
    [task]
  );

  useEffect(() => {
    if (previousTask?.id !== task.id) {
      methods.reset(defaultValues);
    }
  }, [defaultValues, methods, previousTask?.id, task.id]);

  const { time, attributes, fields } = task || {};
  const { task: attTask, crop, tilling_depth, drop_size } = attributes || {};
  const { data: scoresData } = useScores({
    fieldId: fields[0]?.id,
    start: calendarStartDate.getTime() / 1000,
    duration: time.duration,
    task: attTask,
    crop: crop,
    tillingDepth: tilling_depth,
    dropSize: drop_size,
  });

  const { mutate: deleteTask } = useDeleteTaskMutation();

  const [tabIndex, setTabIndex] = useState(0);

  return (
    <>
      <DrawerHeader>
        <Heading as="h2" size="md">
          {t("component.EditTaskDrawer.heading")}
        </Heading>
        <CloseButton onClick={onClose} />
      </DrawerHeader>

      <Flex flex="1" direction="column" overflow="hidden">
        <Tabs
          maxHeight="100%"
          display="flex"
          flexDirection="column"
          index={tabIndex}
          onChange={setTabIndex}
        >
          <TabList>
            <Tab>{t("component.EditTaskDrawer.tabConditionsHeading")}</Tab>
            <Tab>{t("component.EditTaskDrawer.tabEditHeading")}</Tab>
          </TabList>
          <TabPanels overflow="auto">
            <TabPanel padding={"16px 0px"}>
              <DrawerContent>
                {task.attributes.task === "other" ? (
                  <Text>
                    {t(
                      "component.EditTaskDrawer.conditionsNotSupportedForThisTaskType"
                    )}
                  </Text>
                ) : scoresData?.data != null ? (
                  <Conditions
                    task={task}
                    score={dateToScore(
                      scoresData.data,
                      new Date(task.time.start * 1000)
                    )}
                  />
                ) : (
                  /* TODO: Why is skeleton showing when we get a response? */
                  <Skeleton height="100px" />
                )}
              </DrawerContent>
            </TabPanel>
            <TabPanel padding={"16px 0px"}>
              <DrawerContent>
                <TaskForm formMethods={methods} defaultValues={defaultValues} />
              </DrawerContent>
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Flex>

      {tabIndex === 1 && (
        <DrawerFooter>
          <Flex grow={1} gap="10px">
            <Button
              variant="outline"
              colorScheme="red"
              onClick={() => {
                deleteTask(task.id);
              }}
            >
              {t("component.EditTaskDrawer.buttons.delete")}
            </Button>
            <Button onClick={handleSubmit(handleValidSubmit)}>
              {t("component.EditTaskDrawer.buttons.apply")}
            </Button>
          </Flex>
        </DrawerFooter>
      )}
    </>
  );
};
